import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router'

import { useThemeContext } from '@sketch/global-styles'

import { CommandBarHost } from './CommandBarHost'
import {
  CommandBarContext,
  CommandBarContextValue,
  CommandBarRegistrationContext,
  CommandBarRegistrationContextValue,
  defaultCommandBarContextValue,
  defaultCommandBarRegistrationContextValue,
} from '../context'
import { createStaticCommands } from '../commands/createStaticCommands'
import { CommandBarItem, CommandBarOpenSource } from '../types'
import {
  areCommandBarCommandGroupsEqual,
  getCurrentWorkspace,
  getQueryLengthBucket,
  resolveCommandBarPage,
} from '../utils'
import { useCommandBarShortcut, useCommandBarWorkspaceResults } from '../hooks'
import { useAnalytics } from '../../analytics'
import { useUserAuthorizations } from '../../libs/authentication'
import { useGetWorkspaces, useUserSignedIn } from '../../user'

interface CommandBarProviderProps {
  children: React.ReactNode
}

const CommandBarProviderInner = ({ children }: CommandBarProviderProps) => {
  const history = useHistory()
  const location = useLocation()
  const analytics = useAnalytics()
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  // Stack of opened submenu command ids — empty at the top level.
  const [pageStack, setPageStack] = useState<string[]>([])
  const [registeredCommandGroups, setRegisteredCommandGroups] = useState<
    Array<{ commands: CommandBarItem[]; key: string }>
  >([])

  const closeCommandBar = useCallback(() => {
    setIsOpen(false)
    setQuery('')
    setPageStack([])
  }, [])

  const goBack = useCallback(() => {
    setPageStack(stack => stack.slice(0, -1))
    setQuery('')
  }, [])

  const openCommandBar = useCallback(
    (source: CommandBarOpenSource = 'button') => {
      setIsOpen(true)
      analytics.trackEvent('COMMAND BAR - opened', { source })
    },
    [analytics]
  )

  useCommandBarShortcut({
    isOpen,
    onOpen: () => openCommandBar('shortcut'),
    onClose: closeCommandBar,
  })

  // Hide the command bar when the user navigates to a different page
  useEffect(() => {
    closeCommandBar()
  }, [location, closeCommandBar])

  const { workspaces } = useGetWorkspaces({
    fetchPolicy: 'cache-first',
    skip: !isOpen,
  })
  const { hasAccessToWorkspace } = useUserAuthorizations()

  const hasAccessToWorkspaceRef = useRef(hasAccessToWorkspace)
  hasAccessToWorkspaceRef.current = hasAccessToWorkspace

  const validWorkspaces = useMemo(
    () =>
      (workspaces || []).filter(workspace =>
        hasAccessToWorkspaceRef.current({
          identifier: workspace.identifier,
          customer: workspace.customer,
          userRole: workspace.userRole,
        })
      ),
    [workspaces]
  )

  const workspace = useMemo(
    () => getCurrentWorkspace(location.pathname, validWorkspaces),
    [location.pathname, validWorkspaces]
  )

  const registeredCommands = useMemo(
    () =>
      registeredCommandGroups.flatMap(commandGroup => commandGroup.commands),
    [registeredCommandGroups]
  )

  // Functions for theme toggling
  const { enableDarkMode, enableLightMode, toggleMatchSystem, isDarkMode } =
    useThemeContext()

  const enableDarkModeRef = useRef(enableDarkMode)
  const enableLightModeRef = useRef(enableLightMode)
  const toggleMatchSystemRef = useRef(toggleMatchSystem)
  enableDarkModeRef.current = enableDarkMode
  enableLightModeRef.current = enableLightMode
  toggleMatchSystemRef.current = toggleMatchSystem

  const staticCommands = useMemo(
    () =>
      createStaticCommands({
        history,
        workspace,
        workspaces: validWorkspaces,
        appearance: {
          enableDarkMode: () => enableDarkModeRef.current(),
          enableLightMode: () => enableLightModeRef.current(),
          toggleMatchSystem: () => toggleMatchSystemRef.current(),
          isDarkMode,
        },
      }),
    [history, validWorkspaces, workspace, isDarkMode]
  )

  const workspaceResults = useCommandBarWorkspaceResults({
    history,
    isOpen,
    query,
    workspace,
  })

  const runItem = useCallback(
    (item: CommandBarItem, rank: number) => {
      if (item.disabledReason) {
        return
      }

      // A command with children opens its submenu rather than running.
      if (item.children && item.children.length > 0) {
        setPageStack(stack => [...stack, item.id])
        setQuery('')
        return
      }

      analytics.trackEvent('COMMAND BAR - command executed', {
        commandId: item.analyticsId || item.id,
        section: item.section,
        resultType: item.kind,
        rank,
        queryLengthBucket: getQueryLengthBucket(query),
      })

      item.run()

      // Toggles inside a submenu keep the bar open so several options can be
      // flipped in a row; everything else closes on run.
      if (!item.keepOpenOnRun) {
        closeCommandBar()
      }
    },
    [analytics, closeCommandBar, query]
  )

  const registerCommands = useCallback(
    (key: string, commands?: CommandBarItem[]) => {
      setRegisteredCommandGroups(currentCommandGroups => {
        const nextCommandGroup = { commands: commands || [], key }
        const existingCommandGroupIndex = currentCommandGroups.findIndex(
          commandGroup => commandGroup.key === key
        )

        if (existingCommandGroupIndex === -1) {
          return [...currentCommandGroups, nextCommandGroup]
        }

        const existingCommandGroup =
          currentCommandGroups[existingCommandGroupIndex]

        if (
          areCommandBarCommandGroupsEqual(
            existingCommandGroup.commands,
            nextCommandGroup.commands
          )
        ) {
          return currentCommandGroups
        }

        return currentCommandGroups.map(commandGroup =>
          commandGroup.key === key ? nextCommandGroup : commandGroup
        )
      })

      return () => {
        setRegisteredCommandGroups(currentCommandGroups => {
          if (
            !currentCommandGroups.some(commandGroup => commandGroup.key === key)
          ) {
            return currentCommandGroups
          }

          return currentCommandGroups.filter(
            commandGroup => commandGroup.key !== key
          )
        })
      }
    },
    []
  )

  const allItems = useMemo(
    () => [
      ...registeredCommands,
      ...staticCommands,
      ...workspaceResults.results,
    ],
    [registeredCommands, staticCommands, workspaceResults.results]
  )

  // Resolve which page (top level or a submenu) is currently shown.
  const { items: pageItems, breadcrumb } = useMemo(
    () => resolveCommandBarPage(allItems, pageStack),
    [allItems, pageStack]
  )
  const submenuTitle = breadcrumb[breadcrumb.length - 1] ?? null

  const value = useMemo<CommandBarContextValue>(
    () => ({
      error: workspaceResults.error,
      isAvailable: true,
      isOpen,
      items: pageItems,
      loading: workspaceResults.loading,
      openCommandBar,
      closeCommandBar,
      onQueryChange: setQuery,
      query,
      runItem,
      submenuTitle,
      onBack: goBack,
    }),
    [
      closeCommandBar,
      goBack,
      isOpen,
      openCommandBar,
      pageItems,
      query,
      runItem,
      submenuTitle,
      workspaceResults.error,
      workspaceResults.loading,
    ]
  )

  const registrationValue = useMemo<CommandBarRegistrationContextValue>(
    () => ({
      registerCommands,
    }),
    [registerCommands]
  )

  return (
    <CommandBarRegistrationContext.Provider value={registrationValue}>
      <CommandBarContext.Provider value={value}>
        <CommandBarHost />
        {children}
      </CommandBarContext.Provider>
    </CommandBarRegistrationContext.Provider>
  )
}

export const CommandBarProvider = ({ children }: CommandBarProviderProps) => {
  const isSignedIn = useUserSignedIn()

  if (!isSignedIn) {
    return (
      <CommandBarRegistrationContext.Provider
        value={defaultCommandBarRegistrationContextValue}
      >
        <CommandBarContext.Provider value={defaultCommandBarContextValue}>
          {children}
        </CommandBarContext.Provider>
      </CommandBarRegistrationContext.Provider>
    )
  }

  return <CommandBarProviderInner>{children}</CommandBarProviderInner>
}
